/* eslint-disable max-lines-per-function */
import { useMutation, useQuery } from "@apollo/client";
import $ from "jquery";
import { omit } from "lodash";
import React from "react";
import {
  QueryInterestFormData,
  QueryInterestFormDataVariables,
  UpdateInterestFormData,
  UpdateInterestFormDataVariables,
} from "types/gql-types";
import { parseDumbInterestForm } from "utils/interest";
import { useSnackbar } from "components/haven-design/snackbar";
import { useTablesRef } from "components/table-refs";
import InterestUpdateContent from "./interest-update-content";
import { updateCachedQueries } from "./utils";
import UPDATE_INTEREST_DATA from "./update-interest-data.gql";
import QUERY_INTEREST_DATA from "./query-interest-data.gql";

type Props = {
  interestID: string;
  interestListID: string;
  onSubmit: () => Promise<void>;
  onCancel: () => void;
}

const InterestUpdatePage: React.FC<Props> = (props) => {
  const { openSnackbar } = useSnackbar();
  const { interestListID, interestID } = props;
  const [submitIndex, setSubmitIndex] = React.useState(0);

  const variables = { interestId: interestID, interestListId: interestListID };
  const query = useQuery<QueryInterestFormData, QueryInterestFormDataVariables>(
    QUERY_INTEREST_DATA,
    {
      fetchPolicy: "network-only",
      variables,
    },
  );

  const { manualContactTableRef, waitlistUpdateTableRef } = useTablesRef();

  const [updateFormData, updateQuery] = useMutation<
  UpdateInterestFormData, UpdateInterestFormDataVariables>(UPDATE_INTEREST_DATA, {
    refetchQueries: ["QueryInterestProfile", "ApplicantSearch"],
    update: (cache, { data }) => {
      if (data) {
        updateCachedQueries(cache, data, variables);

        [
          manualContactTableRef,
          waitlistUpdateTableRef,
          // @ts-ignore
        ].forEach(ref => ref.current?.onQueryChange());
      }
    },
  });


  if (query.loading) {
    return <span>Loading...</span>;
  }

  if (
    query.error ||
    !(query.data?.applicationFormMetadata?.schema)
  ) {
    return <span>There was a problem loading this page. Please refresh and try again.</span>;
  }

  const {
    data: {
      interest,
      applicationFormMetadata: { schema },
    },
  } = query;

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData: any = omit(parseDumbInterestForm($("form#interest-form")[0], interest), "temp-input");
    const adultCount = Number(formData.applicantAdultCount || 0);
    const childCount = Number(formData.applicantChildCount || 0);
    if (adultCount || childCount) {
      formData.householdCount = adultCount + childCount;
    }

    const updateRequestInterests = interest?.updateRequestInterests || [];
    const requestIsActive = updateRequestInterests
      .some(({ updateRequest: { isActive } }) => isActive);

    updateFormData({
      variables: {
        interestId: props.interestID,
        // @ts-ignore
        lastUpdated: query.data?.interest?.updatedAt,
        keepOnList: requestIsActive && submitIndex === 0,
        data: formData,
      },
    })
      .then((result) =>
        props.onSubmit().then(() => {
          window.scrollTo(0, 0);
          const status = result.data?.pmUpdateCompletedInterest.status;
          if (status === "sync") {
            openSnackbar({ message: "Application details updated.", status: "success" });
          } else {
            openSnackbar({ message: "Application details updated. Dashboard will be reflected momentarily.", status: "warning" });
          }
        }))
      .catch((err) => {
        openSnackbar({ message: err.message, status: "error" });
      });
  };

  return (
    <div id="compensate-padding">
      <div className="panel panel-default">
        <div className="panel-heading">Application details</div>
        <div className="panel-body">
          <InterestUpdateContent
            isSending={updateQuery.loading}
            formData={query.data.interest}
            schema={schema}
            submitBtnIndex={submitIndex}
            setSubmitBtnIndex={(index: number) => setSubmitIndex(index)}
            onChangeHousehold={(event: any) => {
              updateFormData({
                variables: {
                  interestId: props.interestID,
                  // @ts-ignore
                  lastUpdated: query.data?.interest?.updatedAt,
                  data: { householdCount: event.target.value },
                },
              })
                .then(() => window.location.reload())
                .catch((err) => {
                  openSnackbar({ message: err.message, status: "error" });
                });
            }}
            onSubmit={onSubmit}
            onCancel={props.onCancel}
          />
        </div>
      </div>
    </div>
  );
};

export default InterestUpdatePage;
